import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import OidcAuthorizePage from '@/pages/OidcAuthorizePage';
import PaymentOrderPage from '@/pages/PaymentOrderPage';
import PaymentOrderResultPage from '@/pages/PaymentOrderResultPage';
import ContentPage from './pages/ContentPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProtectedRoute from './components/common/layout/ProtectedRoute';
import MainLayout from './components/common/layout/MainLayout';

// 지운 옛 지갑 화면의 주소 (#234)
const LEGACY_WALLET_PATHS = [
  '/subscriptions', '/subscriptions/all',
  '/billing', '/billing/all', '/billing/card-registered',
  '/credits', '/credits/all',
  '/usage', '/usage/all',
  '/payment/success', '/payment/fail',
];

// API 클라이언트의 리디렉션 이벤트를 처리하는 컴포넌트
const AuthRedirectHandler = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    const handleAuthRedirect = (event: CustomEvent<{ path: string }>) => {
      navigate(event.detail.path, { replace: true });
    };
    
    window.addEventListener('auth:redirect', handleAuthRedirect as EventListener);
    return () => {
      window.removeEventListener('auth:redirect', handleAuthRedirect as EventListener);
    };
  }, [navigate]);
  
  return null;
};

function App() {
  // 환경변수에서 base path 가져오기
  // 런타임 환경변수 우선 (Docker 배포 시 주입됨)
  // 폴백: import.meta.env (빌드 타임) → '' (기본값)
  const basename =
    (window as any).__RUNTIME_CONFIG__?.VITE_BASE_PATH?.replace(/\/$/, '') ||
    import.meta.env.VITE_BASE_PATH ||
    '';
  
  return (
    <AuthProvider>
      <Router 
        basename={basename}
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthRedirectHandler />
        <ErrorBoundary>
          <div className="min-h-screen bg-background">
            <Routes>
              {/* 공개 페이지 (로그인 불필요) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/google/callback" element={<LoginPage />} />
              {/* OIDC 현관문 (#142) — 서비스가 사용자를 보내는 주소.
                  로그인 여부를 스스로 판단해 로그인 화면으로 보내므로 ProtectedRoute 밖에 둔다 */}
              <Route path="/oidc/authorize" element={<OidcAuthorizePage />} />
              {/* 결제 창구 — 서비스에서 넘어와 결제한다 (#244) */}
              <Route path="/pay/:orderId" element={<PaymentOrderPage />} />
              <Route path="/pay/:orderId/done" element={<PaymentOrderResultPage mode="done" />} />
              <Route path="/pay/:orderId/fail" element={<PaymentOrderResultPage mode="fail" />} />
              <Route path="/" element={<LoginPage />} />

              {/* 공통 레이아웃 (상단 헤더 포함) */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                {/* 기존 dashboard는 services로 리다이렉트 */}
                <Route path="/dashboard" element={<Navigate to="/services" replace />} />

                {/* 이용자 관리 */}
                <Route path="/users" element={<ContentPage />} />
                <Route path="/users/all" element={<ContentPage />} />
                <Route path="/customers" element={<ContentPage />} />

                {/* 서비스 이용 */}
                <Route path="/services" element={<ContentPage />} />
                <Route path="/service-management" element={<ContentPage />} />
                <Route path="/service-plans" element={<ContentPage />} />
                <Route path="/login-integrations" element={<ContentPage />} />
                <Route path="/activity" element={<ContentPage />} />

                {/* 옛 지갑 화면(구독 · 결제 · 크레딧 · 사용량)은 지웠다 (#234). 즐겨찾기로 와도 빈 화면 대신 서비스 이용으로 */}
                {LEGACY_WALLET_PATHS.map((path) => (
                  <Route key={path} path={path} element={<Navigate to="/services" replace />} />
                ))}

                {/* 설정 */}
                <Route path="/settings" element={<ContentPage />} />
                <Route path="/settings/environment" element={<ContentPage />} />
                <Route path="/settings/email-templates" element={<ContentPage />} />
                <Route path="/settings/ai-providers" element={<ContentPage />} />
                <Route path="/logs" element={<ContentPage />} />
                <Route path="/logs/all" element={<ContentPage />} />
                <Route path="/logs/system" element={<ContentPage />} />
                <Route path="/ai-monitoring" element={<ContentPage />} />
              </Route>

            </Routes>
          </div>
        </ErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;
